import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';

export const ProjectMemberRole = {
  ProjectManager: 100000000,
  TeamMember:     100000001,
  Stakeholder:    100000002,
  Observer:       100000003,
} as const;

export const ROLE_LABEL: Record<number, string> = {
  [ProjectMemberRole.ProjectManager]: 'Project Manager',
  [ProjectMemberRole.TeamMember]:     'Team Member',
  [ProjectMemberRole.Stakeholder]:    'Stakeholder',
  [ProjectMemberRole.Observer]:       'Observer',
};

/** Thrown by the guarded operations when they would leave a project with zero PMs. */
export const LAST_PM_MESSAGE =
  'Every project needs at least one Project Manager — assign another PM before removing or changing this one.';

export interface ProjectMember {
  dagrun_pm_projectmemberid: string;
  dagrun_pm_name: string;
  dagrun_pm_userid: string | null;
  dagrun_pm_username: string | null;
  dagrun_pm_useremail: string | null;
  dagrun_pm_role: number;
  dagrun_pm_isexternal: boolean;
  _dagrun_pm_projectid_value: string;
}

const MEMBER_FIELDS = [
  'dagrun_pm_projectmemberid',
  'dagrun_pm_name',
  'dagrun_pm_userid',
  'dagrun_pm_username',
  'dagrun_pm_useremail',
  'dagrun_pm_role',
  'dagrun_pm_isexternal',
  '_dagrun_pm_projectid_value',
];

export async function getProjectMembers(
  pca: IPublicClientApplication,
  projectId: string,
): Promise<ProjectMember[]> {
  return dvList<ProjectMember>(pca, 'dagrun_pm_projectmembers', {
    select: MEMBER_FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
  });
}

/** The current user's membership row on a project (for role-based nav gating). */
export async function getMyMembership(
  pca: IPublicClientApplication,
  projectId: string,
  userId: string,
): Promise<ProjectMember | null> {
  if (!userId) return null;
  const rows = await dvList<ProjectMember>(pca, 'dagrun_pm_projectmembers', {
    select: MEMBER_FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId} and dagrun_pm_userid eq '${userId}'`,
    top: 1,
  });
  return rows[0] ?? null;
}

export interface CreateProjectMemberInput {
  projectId: string;
  userId: string;
  userName: string;
  userEmail?: string | null;
  role: number;
  isExternal?: boolean;
}

export async function createProjectMember(
  pca: IPublicClientApplication,
  input: CreateProjectMemberInput,
): Promise<string> {
  return dvCreate(pca, 'dagrun_pm_projectmembers', {
    dagrun_pm_name: input.userName.slice(0, 300),
    dagrun_pm_userid: input.userId,
    dagrun_pm_username: input.userName,
    dagrun_pm_useremail: input.userEmail ?? null,
    dagrun_pm_role: input.role,
    dagrun_pm_isexternal: input.isExternal ?? false,
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${input.projectId})`,
  });
}

export async function deleteProjectMember(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_projectmembers', id);
}

export async function updateProjectMemberRole(
  pca: IPublicClientApplication, id: string, role: number,
): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_projectmembers', id, { dagrun_pm_role: role });
}

/** Number of ProjectManager-role members on a project. */
export async function countProjectManagers(
  pca: IPublicClientApplication, projectId: string,
): Promise<number> {
  const rows = await dvList<{ dagrun_pm_projectmemberid: string }>(pca, 'dagrun_pm_projectmembers', {
    select: ['dagrun_pm_projectmemberid'],
    filter: `_dagrun_pm_projectid_value eq ${projectId} and dagrun_pm_role eq ${ProjectMemberRole.ProjectManager}`,
  });
  return rows.length;
}

/**
 * Remove a member, but REFUSE (throw LAST_PM_MESSAGE) if it would drop the
 * project to zero Project Managers. The PM-count is re-read immediately before
 * the delete so the check reflects live state, not stale UI — the enforcement
 * lives in the write path, not only the button. (Client-side, like the rest of
 * this direct-to-Dataverse app; see the Block 3 audit for the systemic caveat.)
 */
export async function removeProjectMemberGuarded(
  pca: IPublicClientApplication, projectId: string, member: ProjectMember,
): Promise<void> {
  if (member.dagrun_pm_role === ProjectMemberRole.ProjectManager) {
    if (await countProjectManagers(pca, projectId) <= 1) throw new Error(LAST_PM_MESSAGE);
  }
  await deleteProjectMember(pca, member.dagrun_pm_projectmemberid);
}

/** Change a member's role, but REFUSE if demoting the last remaining PM. */
export async function setProjectMemberRoleGuarded(
  pca: IPublicClientApplication, projectId: string, member: ProjectMember, newRole: number,
): Promise<void> {
  if (newRole === member.dagrun_pm_role) return;
  if (member.dagrun_pm_role === ProjectMemberRole.ProjectManager && newRole !== ProjectMemberRole.ProjectManager) {
    if (await countProjectManagers(pca, projectId) <= 1) throw new Error(LAST_PM_MESSAGE);
  }
  await updateProjectMemberRole(pca, member.dagrun_pm_projectmemberid, newRole);
}
